import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { getCoachPageUrl } from 'constants/pages';
import { SITE_URL } from 'constants/products';
import { SportsEnum } from 'types/generated/server';
import { getCoachBookingPage } from 'services/server/graphql/queries/getCoachBookingPage';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generatePersonJsonLd } from 'utils/shared/json-ld/person';
import { GeoCoordinates, PostalAddress } from 'utils/shared/json-ld/types';
// import { getAllCoachesPaths } from 'services/server/graphql/queries/getAllCoachesPaths';
import { isUuidFormatValid } from 'utils/shared/string/isUuidFormatValid';
import CoachDetails, { PageProps } from 'screens/CoachDetails';

const REVALIDATE_IN_SECONDS = 30 * 60 * 1;
const IS_ALL_AS_ISR = true;
const FAKE_UUID = '00000000-0000-0000-0000-000000000000';

const getJsonLdDescription = (name: string) => {
  return `${name} is a pickleball coach providing lessons through Bounce.`;
};

export const getStaticProps: GetStaticProps<PageProps, { usernameOrId: string }> = async ({
  params,
}) => {
  const usernameOrId = params?.usernameOrId;

  console.log({ usernameOrId });

  /**
   * @todo Also check coach starter profiles if the coach is not found
   */

  if (!usernameOrId) {
    console.error('No usernameOrId provided');
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    const userByUsernameResponse = await getCoachBookingPage({
      username: usernameOrId.toLowerCase(),
      id: isUuidFormatValid(usernameOrId) ? usernameOrId : FAKE_UUID,
      startDateTime: new Date().toISOString(),
    });

    const coach = userByUsernameResponse?.userProfiles?.[0];

    const coachStarterProfile = userByUsernameResponse?.coachStarterProfilesByPk;

    if (!coach && !coachStarterProfile) {
      console.log('No coach found', usernameOrId);
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    const socialMedia: string[] = [];
    const isTennisCoach = coach?.defaultSport === SportsEnum.Tennis;

    // if (coach?.instagramUrl) {
    //   socialMedia.push(coach?.instagramUrl);
    // }
    // if (coach?.facebookUrl) {
    //   socialMedia.push(coach?.facebookUrl);
    // }
    // if (coach?.xUrl) {
    //   socialMedia.push(coach?.xUrl);
    // }
    // if (coach?.linkedinUrl) {
    //   socialMedia.push(coach?.linkedinUrl);
    // }
    // if (coach?.tiktokUrl) {
    //   socialMedia.push(coach?.tiktokUrl);
    // }
    // if (coach?.youtubeUrl) {
    //   socialMedia.push(coach?.youtubeUrl);
    // }

    if (coachStarterProfile?.instagram) {
      socialMedia.push(coachStarterProfile.instagram);
    }
    if (coachStarterProfile?.facebook) {
      socialMedia.push(coachStarterProfile.facebook);
    }
    if (coachStarterProfile?.twitter) {
      socialMedia.push(coachStarterProfile.twitter);
    }
    if (coachStarterProfile?.youtube) {
      socialMedia.push(coachStarterProfile.youtube);
    }
    if (coachStarterProfile?.linkedin) {
      socialMedia.push(coachStarterProfile.linkedin);
    }

    let geo: GeoCoordinates | undefined = undefined;

    if (coach?.defaultCoachVenue?.latitude && coach?.defaultCoachVenue?.longitude) {
      geo = {
        latitude: coach?.defaultCoachVenue?.latitude,
        longitude: coach?.defaultCoachVenue?.longitude,
      };
    } else if (coach?.activeCity?.geometry) {
      geo = {
        latitude: coach?.activeCity?.geometry?.coordinates?.[1],
        longitude: coach?.activeCity?.geometry?.coordinates?.[0],
      };
    } else if (coachStarterProfile?.latitude && coachStarterProfile?.longitude) {
      geo = {
        latitude: coachStarterProfile.latitude,
        longitude: coachStarterProfile.longitude,
      };
    }

    const cityName = coach?.activeCity?.name;
    const regionName = coach?.activeCity?.countrySubdivision?.name;
    const postalAddress: PostalAddress = {
      ...(coach?.defaultCoachVenue?.addressString && {
        streetAddress: coach.defaultCoachVenue.addressString,
      }),
      ...(cityName && { addressLocality: cityName }),
      ...(regionName && { addressRegion: regionName }),
      addressCountry: 'US',
    };
    const hasStructuredAddress =
      Object.keys(postalAddress).filter((k) => k !== 'addressCountry').length > 0;
    const fallbackAddressString =
      coach?.defaultCoachVenue?.addressString ||
      cityName ||
      coachStarterProfile?.addressDisplay ||
      undefined;

    const priceCents = coach?.priceUnitAmountCoachDefault ?? 0;
    const priceDollars = priceCents / 100;
    const priceCategorySymbol =
      priceDollars > 0
        ? priceDollars < 25
          ? '$'
          : priceDollars < 75
            ? '$$'
            : priceDollars < 150
              ? '$$$'
              : '$$$$'
        : undefined;

    const sportsKnown: string[] = [];
    if (coach?.coachSports && coach.coachSports.length > 0) {
      coach.coachSports.forEach((cs) => {
        const sportName = cs.sport
          ? cs.sport.charAt(0) + cs.sport.slice(1).toLowerCase()
          : '';
        if (sportName && !sportsKnown.includes(sportName)) sportsKnown.push(sportName);
      });
    }
    if (sportsKnown.length === 0) {
      sportsKnown.push(isTennisCoach ? 'Tennis' : 'Pickleball');
    }
    sportsKnown.push('Fitness');

    const credentials: Array<{ name: string; credentialCategory: string }> = [];
    if (coach?.pprId) {
      credentials.push({ name: 'PPR Certified Professional', credentialCategory: 'certification' });
    }
    if (coach?.isIptpaMember || coach?.iptpaId) {
      credentials.push({ name: 'IPTPA Member', credentialCategory: 'certification' });
    }
    if (coach?.isPciMember || coach?.pciId) {
      credentials.push({ name: 'PCI Member', credentialCategory: 'certification' });
    }
    if (coach?.isQuatroPickleballMember || coach?.quatroPickleballMemberId) {
      credentials.push({ name: 'Quatro Pickleball Member', credentialCategory: 'membership' });
    }

    const personJsonLd = generatePersonJsonLd({
      name: coach?.fullName || coachStarterProfile?.name || '',
      url: `${process.env.APP_URL}${getCoachPageUrl(usernameOrId.toLowerCase())}`,
      image: coach?.profileImageProviderUrl || coachStarterProfile?.profileImageUrl || undefined,
      description:
        coach?.aboutMe ||
        coachStarterProfile?.aboutMe ||
        getJsonLdDescription(coach?.fullName || coachStarterProfile?.name || ''),
      jobTitle: isTennisCoach ? 'Tennis Coach' : 'Pickleball Coach',
      hasOccupation: 'Coach',
      address: hasStructuredAddress ? postalAddress : fallbackAddressString,
      geo: geo,
      priceRange: priceCategorySymbol,
      worksFor: {
        name: 'Bounce',
        url: SITE_URL,
      },
      knowsAbout: sportsKnown,
      sameAs: socialMedia.length > 0 ? socialMedia : undefined,
      hasCredential: credentials.length > 0 ? credentials : undefined,
    });

    const coachUrl = `${SITE_URL}${getCoachPageUrl(usernameOrId.toLowerCase())}`;
    const coachName = coach?.fullName || coachStarterProfile?.name || '';
    const breadcrumbsJsonLd = generateBreadcrumbListJsonLd({
      items: [
        { id: SITE_URL, name: 'Home' },
        ...(coachName ? [{ id: coachUrl, name: coachName }] : []),
      ],
    });

    const combinedSchemas = combineJsonLdSchemas([personJsonLd, breadcrumbsJsonLd]);

    return {
      props: {
        coach: coach || null,
        coachStarterProfile,
        usernameOrId,
        jsonLd: JSON.stringify(combinedSchemas),
      },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    console.log('build error', error);
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  /**
   * @note use fallback SSG
   */
  // const { userProfiles } = await getAllCoachesPaths();

  // const paths = userProfiles.map((profile: any) => ({ params: { usernameOrId: profile.username } }));

  return {
    paths: [],
    fallback: true,
  };
};

export default CoachDetails;
